import React, { useState } from 'react';
import { object, func, string, bool, number } from 'prop-types';
import { get, map, filter, head, includes, size, find, toString, isEmpty, some, lowerCase, trim } from 'lodash';
import moment from 'moment';
import { FormSelect, FlexContainer, Text, FormDatePicker, FormInput, SalesChannelSelect, Loading } from '../../common';
import { Query } from '../../graphql';
import { GET_USER_ADDRESSES, GET_SCHEDULE_WINDOW, GET_WINDOWS_FROM_LOCAL } from '../../../queries';
import { withApollo } from 'react-apollo';
import { holidays } from '../../../utils/misc';
import { CHANGEABLE_SLAS, canChangeSla, requiresCustomerAddress } from './rescheduleHelpers';

const formatPromiseRange = (start, end) => (start && end ? `${start} - ${end}` : null);

const mapCapacityWindow = (window) => {
    const promiseRange = formatPromiseRange(get(window, 'startWindowPromise'), get(window, 'endWindowPromise'));
    const legacyRange = formatPromiseRange(get(window, 'startWindow'), get(window, 'endWindow'));
    const range = get(window, 'fullWindow') || promiseRange || legacyRange;
    const value = get(window, 'value');

    return {
        fullWindow: range,
        label: get(window, 'label') || range,
        value: value != null ? value : range
    };
};

const getCapacityDeliveryWindows = (carrier, date) => {
    const direct = get(carrier, 'deliveryWindows', []);
    if (size(direct) > 0) return direct;
    const dateEntry = find(get(carrier, 'dates', []), { date });
    return get(dateEntry, 'deliveryWindows', []);
};

const NewShipping = ({
    onChangeComments,
    shippingStore,
    customerAddress,
    userProfileId,
    onChangeShippingDate,
    shippingTime,
    onChangeShippingTime,
    shippingDate,
    setShippingStore,
    setCustomerAddress,
    logisticsInfo,
    shippingType: slaType,
    onChangeShippingType,
    salesChannel,
    selectedAddress,
    isCapacity,
    storeKind,
    itemCount
}) => {
    const store = get(shippingStore, 'data.id', null);
    const originalSla = get(logisticsInfo, 'selectedSla', null);
    const defaultDate = moment(get(logisticsInfo, 'shippingETA'), 'YYYY-MM-DD HH:mm')
    const [windowsFromLocal, setWindowsFromLocal] = useState([]);
    const [selectedCarrier, setSelectedCarrier] = useState(null);
    const canChangeSlaType = canChangeSla(originalSla);

    const fieldErrors = (type) => {
        switch (type) {
        case 'customerAddress':
            return 'No existen direcciones asociadas a éste cliente.';
        case 'shippingStore':
            return 'Temporalmente sin cobertura, intenta con otra dirección';
        case 'shippingStore-scheduling':
            return 'Temporamente sin cobertura, intenta con otro Tipo de envío';
        case 'shippingTime':
        case 'selectedCarrier':
            return 'No hay ventanas disponibles para ésta fecha seleccionada.';
        case 'defaultCarrierIsntIntoWindowsFromLocal':
            return 'La transportadora original no se encuentra disponible para esta fecha';
        default:
            return 'Error.';
        }
    };

    const disabledShippingDates = (current) => {
        const date = moment();
        date.hour(0);
        date.minute(0);
        date.second(0);

        // bloquea si cada item del calendario es menor a la fecha del shipping
        // if (current.valueOf() < initDate.valueOf()) return true;

        // bloquea si la fecha de shipping es menor a hoy y que cada item del calendario sea menor a hoy
        // if (initDate.valueOf() < date.valueOf() && current.valueOf() < date.valueOf()) return true;

        // bloquea si el tipo de delivery es retiro en tienda o click al auto, y si el dia del item es un domingo
        if (includes(['retiro en tienda', 'click al auto'], lowerCase(slaType)) && current.weekday() === 6) return true;

        // bloquea los feriados irrenunciables
        if (includes(holidays, `${current.date()}/${current.month() + 1}`)) return true;

        // bloquea si la fecha de cada item es menor a hoy
        if (current.valueOf() < date.valueOf()) return true;

        // bloquea si la fecha del item actual es mayor a la fecha final de shipping permitido
        // if (current.valueOf() > endDate.valueOf()) return true;
    };

    const shippingLocation = () => {
        if (!requiresCustomerAddress(slaType)) return null;

        return (
            <Query
                query={GET_USER_ADDRESSES}
                variables={{ userProfileId }}
                onCompleted={(data) => {
                    const getUserAddresses = get(data, 'getUserAddresses', []);
                    const getGeoCoordinateFromSelectedAddress = index => `${get(selectedAddress, `geoCoordinates[${index}]`)}`;
                    const defaultSelected = find(getUserAddresses, ({ geoCoordinates }) => ((geoCoordinates[0] === getGeoCoordinateFromSelectedAddress(0)) && (geoCoordinates[1] === getGeoCoordinateFromSelectedAddress(1))));
                    setCustomerAddress({
                        label: get(defaultSelected, 'addressLine'),
                        value: get(defaultSelected, 'id'),
                        geoLocation: get(defaultSelected, 'geoLocation', []),
                        data: { ...defaultSelected }
                    });
                }}
            >
                {({ data, loading }) => {
                    const getUserAddresses = get(data, 'getUserAddresses', []);
                    const options = map(getUserAddresses, ({ addressLine, geoLocation, id }) => {
                        return {
                            label: addressLine,
                            geoLocation,
                            value: id
                        };
                    });
                    return (
                        <FormSelect
                            label='Dirección'
                            value={customerAddress}
                            options={options}
                            onChange={({ value }) => setCustomerAddress({
                                ...value,
                                geoLocation: get(head(filter(options, ({ value: id }) => id === get(value, 'value'))), 'geoLocation', {}),
                                data: head(filter(getUserAddresses, ({ id }) => id === get(value, 'value')))
                            })}
                            loading={loading}
                            error={size(options) < 1 && fieldErrors('customerAddress')}
                        />
                    );
                }}
            </Query>
        );
    };

    const remoteStore = () => {
        return (
            <SalesChannelSelect
                native
                label='Tienda'
                salesChannel={salesChannel}
                customizeOptions={(options) => {
                    return filter(options, ({ kind }) => kind === storeKind)
                }}
                onCompleted={({ value, data }) => {
                    const defaultSalesChannelData = find(data, ({ id }) => includes([get(value, 'value')], id));
                    setShippingStore({
                        label: get(value, 'label'),
                        value: get(value, 'value'),
                        data: {
                            title: get(value, 'label'),
                            id: get(defaultSalesChannelData, 'store'),
                            comercial_policy: parseInt(get(value, 'value')),
                            slaType: 'Despacho'
                        }
                    });
                }}
                onChange={({ value, data }) => {
                    setShippingStore({
                        label: get(value, 'label'),
                        value: get(value, 'value'),
                        data: {
                            title: get(value, 'label'),
                            id: get(data, 'store'),
                            comercial_policy: parseInt(get(value, 'value')),
                            slaType: 'Despacho'
                        }
                    });
                    setSelectedCarrier(null);
                    onChangeShippingTime(null);
                }}
            />
        );
    };

    const slaTypeSelector = () => {
        if (!canChangeSlaType) {
            return <Text semibold size={13} text={slaType} color='#969696' paddingTop={10} />;
        }

        return (
            <FormSelect
                label='Tipo de envío'
                value={{ label: slaType, value: slaType }}
                options={map(CHANGEABLE_SLAS, sla => ({ label: sla, value: sla }))}
                onChange={({ value }) => {
                    onChangeShippingType(get(value, 'value', slaType));
                    setSelectedCarrier(null);
                    setWindowsFromLocal([]);
                    onChangeShippingTime(null);
                }}
            />
        );
    };

    const shippingDateBox = () => {
        const disallowedShippingTypes = ['Despacho Express']

        if (includes(disallowedShippingTypes, slaType)) return null;

        return (
            <FormDatePicker
                style={{ minWidth: '100%' }}
                label='Nueva fecha de envío'
                defaultValue={defaultDate}
                showDateInput={false}
                disabledDate={disabledShippingDates}
                portalStyle={{ position: 'absolute', zIndex: 1, top: '5px' }}
                onChange={values => {
                    onChangeShippingDate(values);
                }}
            />
        );
    };

    const capacityWrapper = () => {
        const disallowedShippingTypes = ['Despacho Express']
        const date = moment(shippingDate[0]).format('YYYY-MM-DD');
        const totalItems = Number(itemCount);

        if (!isCapacity || !date || !store || !isFinite(totalItems) || includes(disallowedShippingTypes, slaType)) return null;

        return (
            <Query
                query={GET_WINDOWS_FROM_LOCAL}
                fetchPolicy='network-only'
                variables={{ input: { date, storeCode: store, slaType, totalItems } }}
                onCompleted={({ getWindowsFromLocal }) => {
                    setWindowsFromLocal(get(getWindowsFromLocal, 'data', []));
                    // Initial carrier value needs be setted to null to force user select a new carrier
                    // If origin carrier is into fetched windows, this value will be overwriten by carrier select default value
                    setSelectedCarrier(null);
                }}
            >
                {({ loading }) => {
                    if (loading) return <Loading loading />;
                    const defaultCarrier = get(logisticsInfo, 'deliveryIds[0].courierName', '');
                    const defaultCarrieIsIntoWindowsFromLocal = some(windowsFromLocal, { 'carrier': defaultCarrier });
                    const formSelectAditionalProps = defaultCarrieIsIntoWindowsFromLocal && !selectedCarrier ? { defaultValue: { label: defaultCarrier, value: defaultCarrier } } : {};

                    return (
                        <FormSelect
                            label='Transportadora'
                            options={map(windowsFromLocal, ({ carrier, carrierId }) => ({ label: carrier, value: carrierId }))}
                            {...formSelectAditionalProps}
                            onChange={({ value }) => {
                                setSelectedCarrier(value)
                                onChangeShippingTime(null);
                            }}
                            value={selectedCarrier}
                            error={(() => {
                                if (isEmpty(windowsFromLocal)) return fieldErrors('selectedCarrier');
                                if (!some(windowsFromLocal, { 'carrier': defaultCarrier }) && isEmpty(get(selectedCarrier, 'label'))) return fieldErrors('defaultCarrierIsntIntoWindowsFromLocal');
                            })()}
                        />
                    );
                }}
            </Query>
        );
    };

    const scheludeWindow = () => {
        const disallowedShippingTypes = ['Despacho Express']

        if (includes(disallowedShippingTypes, slaType) || !store || !shippingDate || (isCapacity && isEmpty(selectedCarrier))) return null;

        const newDate = toString(moment(head(shippingDate)).day());

        // This function builds custom options for capacity scheduling cases
        const shippingTimeOptionsFromCapacity = () => {
            const date = moment(head(shippingDate)).format('YYYY-MM-DD');
            const windowsResult = find(windowsFromLocal, ({ carrierId }) => carrierId === get(selectedCarrier, 'value'));

            return map(getCapacityDeliveryWindows(windowsResult, date), (window) => {
                const mapped = mapCapacityWindow(window);
                return {
                    courierName: get(windowsResult, 'carrier'),
                    courierId: get(windowsResult, 'carrierId'),
                    fullWindow: mapped.fullWindow,
                    label: mapped.label,
                    value: mapped.value
                };
            });
        };

        return (
            <Query
                query={GET_SCHEDULE_WINDOW}
                fetchPolicy='network-only'
                variables={{
                    newDate,
                    slaType: includes(slaType, 'Especial') ? 'Despacho Especial' : trim(slaType),
                    store
                }}
                skip={isCapacity} // This query willnt be fetched running capacity scheduling
            >
                {({ data, loading }) => {
                    const shippingTimeOptions = isCapacity
                        ? shippingTimeOptionsFromCapacity()
                        : map(get(data, 'getScheduleWindow'), ({ window, id, ...fields }) => ({ label: window, value: id, ...fields }));
                    return (
                        <FormSelect
                            label='Nuevo horario'
                            value={shippingTime}
                            options={shippingTimeOptions}
                            onChange={({ value }) => {
                                const timeValue = get(value, 'label', moment().format('HH:mm'));
                                const timeProperties = head(filter(shippingTimeOptions, ({ value: optionId }) => optionId === get(value, 'value', 1)));
                                onChangeShippingTime({ ...timeProperties, label: timeValue, value: get(timeProperties, 'fullWindow') });
                            }}
                            loading={loading}
                            error={size(shippingTimeOptions) < 1 && fieldErrors('shippingTime')}
                        />
                    );
                }}
            </Query>
        );
    };

    return (
        <FlexContainer mY={15} direction='column' mR={15}>
            <Text semibold size={13} text='Nueva información de envío' />
            { slaTypeSelector() }
            { shippingLocation() }
            { remoteStore() }
            { shippingDateBox() }
            { capacityWrapper() }
            { scheludeWindow() }
            <FormInput
                label='Motivo'
                textArea
                onChange={event => onChangeComments(get(event, 'target.value', null))}
            />
        </FlexContainer>
    );
};

NewShipping.propTypes = {
    customerAddress: object,
    shippingDate: string,
    onChangeShippingDate: func,
    onChangeComments: func,
    shippingStore: object,
    setShippingStore: func,
    shippingTime: object,
    onChangeShippingTime: func,
    userProfileId: string,
    orderId: string,
    isCapacity: bool,
    itemCount: number,
    storeKind: string,
    shippingType: string,
    onChangeShippingType: func
};

export default withApollo(NewShipping);
